import type { InvoiceFormData, ValidationError } from '../types/invoice';

export function validateInvoice(data: InvoiceFormData): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!data.invoiceNumber?.trim()) {
    errors.push({ field: 'invoiceNumber', message: 'Invoice number is required' });
  }

  if (!data.invoiceDate?.trim()) {
    errors.push({ field: 'invoiceDate', message: 'Invoice date is required' });
  }

  if (!data.currencyCode?.trim()) {
    errors.push({ field: 'currencyCode', message: 'Currency code is required' });
  }

  if (!data.purchaseOrderNumber?.trim()) {
    errors.push({ field: 'purchaseOrderNumber', message: 'Purchase order number is required' });
  }

  // ── Required parties ──────────────────────────────────────────────────
  const billToParty = data.names.find(p => p.type === 'BT');
  if (!billToParty) {
    errors.push({ field: 'names_BT', message: 'Bill To (BT) party is required', section: 'names' });
  }

  const sellingParty = data.names.find(p => p.type === 'SE');
  if (!sellingParty) {
    errors.push({ field: 'names_SE', message: 'Selling Party (SE) is required', section: 'names' });
  } else {
    if (!sellingParty.name?.trim()) errors.push({ field: 'names_SE_name', message: 'Selling Party: name is required', section: 'names' });
    if (!sellingParty.id?.trim()) errors.push({ field: 'names_SE_id', message: 'Selling Party: ID is required', section: 'names' });
  }

  const shipFromParty = data.names.find(p => p.type === 'SF');
  if (!shipFromParty) {
    errors.push({ field: 'names_SF', message: 'Ship From (SF) party is required', section: 'names' });
  } else {
    if (!shipFromParty.name?.trim()) errors.push({ field: 'names_SF_name', message: 'Ship From: name is required', section: 'names' });
    if (!shipFromParty.address1?.trim()) errors.push({ field: 'names_SF_address', message: 'Ship From: address is required', section: 'names' });
    if (!shipFromParty.city?.trim()) errors.push({ field: 'names_SF_city', message: 'Ship From: city is required', section: 'names' });
    if (!shipFromParty.state?.trim()) errors.push({ field: 'names_SF_state', message: 'Ship From: state is required', section: 'names' });
    if (!shipFromParty.postalCode?.trim()) errors.push({ field: 'names_SF_zip', message: 'Ship From: postal code is required', section: 'names' });
    if (!shipFromParty.country?.trim()) errors.push({ field: 'names_SF_country', message: 'Ship From: country is required', section: 'names' });
  }

  const shipToParty = data.names.find(p => p.type === 'ST');
  if (!shipToParty) {
    errors.push({ field: 'names_ST', message: 'Ship To (ST) party is required', section: 'names' });
  } else {
    if (!shipToParty.name?.trim()) errors.push({ field: 'names_ST_name', message: 'Ship To: name is required', section: 'names' });
    if (!shipToParty.address1?.trim()) errors.push({ field: 'names_ST_address', message: 'Ship To: address is required', section: 'names' });
    if (!shipToParty.city?.trim()) errors.push({ field: 'names_ST_city', message: 'Ship To: city is required', section: 'names' });
    if (!shipToParty.state?.trim()) errors.push({ field: 'names_ST_state', message: 'Ship To: state is required', section: 'names' });
    if (!shipToParty.postalCode?.trim()) errors.push({ field: 'names_ST_zip', message: 'Ship To: postal code is required', section: 'names' });
    if (!shipToParty.country?.trim()) errors.push({ field: 'names_ST_country', message: 'Ship To: country is required', section: 'names' });
  }

  // ── Required dates ───────────────────────────────────────────────────
  const shipDate = data.dates.find(d => d.qualifier === '011');
  if (!shipDate || !shipDate.date?.trim()) {
    errors.push({ field: 'dates_011', message: 'Ship date (DTM:011) is required', section: 'dates' });
  }

  // ── Required references ──────────────────────────────────────────────
  const packingListRef = data.references.find(r => r.qualifier === 'PK');
  if (!packingListRef || !packingListRef.id?.trim()) {
    errors.push({ field: 'references_PK', message: 'Packing list reference (REF:PK) is required', section: 'references' });
  }

  // ── Line items ───────────────────────────────────────────────────────
  if (data.details.length === 0) {
    errors.push({ field: 'details', message: 'At least one line item is required' });
  }

  data.details.forEach((item, index) => {
    if (!item.quantity?.trim()) {
      errors.push({
        field: `detail_${index}_quantity`,
        message: `Line ${item.lineNumber}: Quantity is required`,
        section: 'details'
      });
    }

    if (!item.unitPrice?.trim()) {
      errors.push({
        field: `detail_${index}_unitPrice`,
        message: `Line ${item.lineNumber}: Unit price is required`,
        section: 'details'
      });
    }

    if (!item.extendedAmount?.trim()) {
      errors.push({
        field: `detail_${index}_extendedAmount`,
        message: `Line ${item.lineNumber}: Extended amount is required`,
        section: 'details'
      });
    }

    if (item.parts.length === 0) {
      errors.push({
        field: `detail_${index}_parts`,
        message: `Line ${item.lineNumber}: At least one part identification is required`,
        section: 'details'
      });
    }
  });

  if (!data.summary.invoiceTotal?.trim()) {
    errors.push({ field: 'invoiceTotal', message: 'Invoice total is required', section: 'summary' });
  }

  return errors;
}

export function getErrorsAsRecord(errors: ValidationError[]): Record<string, string> {
  const record: Record<string, string> = {};
  errors.forEach(error => {
    record[error.field] = error.message;
  });
  return record;
}

export function calculateLineItemTotal(quantity: string, unitPrice: string): string {
  const qty = parseFloat(quantity) || 0;
  const price = parseFloat(unitPrice) || 0;
  return (qty * price).toFixed(2);
}

export function calculateSummary(data: InvoiceFormData): void {
  let subtotal = 0;
  let totalTax = 0;
  let totalAllowances = 0;
  let totalCharges = 0;

  data.details.forEach(item => {
    const lineTotal = parseFloat(item.extendedAmount) || 0;
    subtotal += lineTotal;

    item.taxes.forEach(tax => {
      totalTax += parseFloat(tax.amount) || 0;
    });

    item.allowancesCharges.forEach(ac => {
      if (ac.indicator === 'A') {
        totalAllowances += parseFloat(ac.amount) || 0;
      } else {
        totalCharges += parseFloat(ac.amount) || 0;
      }
    });
  });

  data.headerAllowancesCharges.forEach(ac => {
    if (ac.indicator === 'A') {
      totalAllowances += parseFloat(ac.amount) || 0;
    } else {
      totalCharges += parseFloat(ac.amount) || 0;
    }
  });

  data.headerTaxes.forEach(tax => {
    totalTax += parseFloat(tax.amount) || 0;
  });

  const invoiceTotal = subtotal + totalTax + totalCharges - totalAllowances;

  data.summary = {
    totalLineItems: data.details.length,
    subtotal: subtotal.toFixed(2),
    totalTax: totalTax.toFixed(2),
    totalAllowances: totalAllowances.toFixed(2),
    totalCharges: totalCharges.toFixed(2),
    invoiceTotal: invoiceTotal.toFixed(2)
  };
}
